import { useState } from 'react';
import { BoundedContent, Box, H2 } from '@vp/swan';
import { SupportedLocale, __, __url } from '@99designs/i18n';
import { usePage } from '@99designs/tracking';
import { getNavigationData, useCrossPlatformTracking } from '../../../products/tracking';
import { isBifrostDesignProduct, isNNDesignProduct } from '../../../products/types';
import { ProductCarousel } from '../ProductCarousel';
import { ScrollableSelectionSet } from '../ScrollableSelectionSet';
import { SeeAllTile } from './SeeAllTile/SeeAllTile';
import type { DesignProductPresenter } from '../../../products/types';
import type { Category } from './categoriesConfig';

interface DesignCategoriesProps {
    title: string;
    categories: Category[];
    initialCategoryId?: string;
    locale: SupportedLocale;
}

export function DesignCategories({
    title,
    categories,
    initialCategoryId,
    locale,
}: DesignCategoriesProps) {
    const { pageData } = usePage();
    const [selectedCategoryId, setSelectedCategoryId] = useState(() => {
        if (categories.length === 0) {
            return '';
        }
        return (
            categories.find((category) => category.id === initialCategoryId)?.id ?? categories[0].id
        );
    });
    const selectedCategory = categories.find((category) => category.id === selectedCategoryId);
    const categoryPageEnabled = selectedCategory?.pageConfig?.availableLocales.includes(locale);

    const pageZone = 'Design Category Tiles';
    const crossPlatformTracking = useCrossPlatformTracking(locale, pageZone);
    return (
        <BoundedContent>
            <Box paddingLeft={{ xs: 3, md: 0 }} paddingRight={{ xs: 3, md: 0 }}>
                <H2 marginBottom="6">{title}</H2>
                <ScrollableSelectionSet
                    options={categories.map((category) => ({
                        id: category.id,
                        label: category.name,
                    }))}
                    selectedValue={selectedCategoryId}
                    setSelectedValue={(value) => setSelectedCategoryId(value ?? categories[0].id)}
                ></ScrollableSelectionSet>
                <Box>
                    <ProductCarousel
                        // Do not remove the key prop, it is required for the carousel to re-render when the selected category changes
                        key={selectedCategoryId}
                        products={selectedCategory?.products ?? []}
                        locale={locale}
                        maxSlides={6}
                        SeeAllTile={
                            categoryPageEnabled && selectedCategory?.pageConfig?.path ? (
                                <SeeAllTile
                                    href={__url(selectedCategory?.pageConfig?.path)}
                                    trackingData={{
                                        category: pageData.pageSection,
                                        label: `Content: ${pageZone}`,
                                        eventDetail: `${pageData.pagePath};${selectedCategory?.pageConfig?.path};${pageZone};${selectedCategory?.pageConfig?.path}`,
                                        navigationDetail: `${selectedCategory?.name}|See all:${selectedCategory?.products.length}`,
                                        pageSection: pageData.pageSection,
                                        pageStage: pageData.pageStage,
                                        pageName: pageData.pageName,
                                    }}
                                />
                            ) : undefined
                        }
                        productClickedTrackingData={(product: DesignProductPresenter, index) => {
                            return getNavigationData(product, {
                                pageName: pageData.pageName,
                                pageStage: pageData.pageStage,
                                pageSection: pageData.pageSection,
                                pagePath: pageData.pagePath ?? window.location.pathname,
                                pageZone,
                                position: index,
                                linkHierarchy: selectedCategory?.name,
                                crossPlatformTracking:
                                    isNNDesignProduct(product) || isBifrostDesignProduct(product)
                                        ? crossPlatformTracking?.toString()
                                        : undefined,
                            });
                        }}
                    />
                </Box>
            </Box>
        </BoundedContent>
    );
}
